// Conversational SEO — Elementor content extraction + editing.
//
// Elementor stores a page's content as a JSON tree in the `_elementor_data` postmeta, NOT in
// post_content — so the standard REST content read/write can't see or change it. The LSG plugin
// exposes that tree via GET /seo-audit/v1/builder/{id} and saves an edited tree via
// POST /seo-audit/v1/elementor/{id} (using Elementor's own document API, which regenerates the
// cached CSS so edits appear on the front end). These helpers walk that tree to:
//   • read the page's real text, editable blocks and links for analysis, and
//   • apply targeted edits (by block id/field) or global find/replace, mutating the tree in place
//     so the caller can post it straight back.
//
// A node has an `id`, an `elType` (section/column/container/widget) and, for widgets, a
// `widgetType` whose text lives in `settings`. We address text by (element id, field name).

export interface ElBlock { id: string; widget: string; field: string; text: string; isHtml: boolean }
export interface ElLink { url: string; anchor: string }
export interface ElEdit { id: string; field?: string; text: string }
export interface ElFindReplace { find: string; replace: string }

interface ElNode {
  id?: string;
  elType?: string;
  widgetType?: string;
  settings?: Record<string, unknown>;
  elements?: ElNode[];
  [k: string]: unknown;
}

// Text-bearing scalar setting fields per Elementor widget type. `html:true` marks a rich-text
// (HTML) field; the rest are plain strings. Covers the common content widgets; anything not
// listed here is still reachable via find/replace.
const TEXT_FIELDS: Record<string, Array<{ field: string; html: boolean }>> = {
  "heading":            [{ field: "title", html: false }],
  "text-editor":        [{ field: "editor", html: true }],
  "theme-post-content": [{ field: "editor", html: true }],
  "button":             [{ field: "text", html: false }],
  "icon-box":           [{ field: "title_text", html: false }, { field: "description_text", html: true }],
  "image-box":          [{ field: "title_text", html: false }, { field: "description_text", html: true }],
  "call-to-action":     [{ field: "title", html: false }, { field: "description", html: true }],
  "testimonial":        [{ field: "testimonial_content", html: true }, { field: "testimonial_name", html: false }, { field: "testimonial_job", html: false }],
  "blockquote":         [{ field: "blockquote_content", html: true }],
  "alert":              [{ field: "alert_title", html: false }, { field: "alert_description", html: true }],
  "counter":            [{ field: "title", html: false }],
  "progress":           [{ field: "title", html: false }],
};

const stripHtml = (html: string): string => html.replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();

// One addressable text value in the tree, with a live setter that mutates the underlying node.
interface Visit { id: string; widget: string; field: string; text: string; isHtml: boolean; set: (v: string) => void }

// Collect every editable text value in document order. Handles scalar widget fields plus the two
// most common repeaters (tabs/accordion/toggle → `tabs[]`, icon-list → `icon_list[]`), each
// addressed with an indexed field name (e.g. "tabs.0.tab_content") so edits can target them.
function visitTextFields(elements: ElNode[]): Visit[] {
  const out: Visit[] = [];
  const walk = (nodes: ElNode[]): void => {
    for (const n of nodes) {
      const s = n.settings;
      const widget = n.widgetType ?? "";
      if (s && widget) {
        for (const { field, html } of TEXT_FIELDS[widget] ?? []) {
          const v = s[field];
          if (typeof v === "string") out.push({ id: n.id ?? "", widget, field, text: v, isHtml: html, set: nv => { s[field] = nv; } });
        }
        if (Array.isArray(s.tabs)) {
          (s.tabs as Array<Record<string, unknown>>).forEach((t, idx) => {
            if (t && typeof t.tab_title === "string") out.push({ id: n.id ?? "", widget, field: `tabs.${idx}.tab_title`, text: t.tab_title, isHtml: false, set: nv => { t.tab_title = nv; } });
            if (t && typeof t.tab_content === "string") out.push({ id: n.id ?? "", widget, field: `tabs.${idx}.tab_content`, text: t.tab_content, isHtml: true, set: nv => { t.tab_content = nv; } });
          });
        }
        if (Array.isArray(s.icon_list)) {
          (s.icon_list as Array<Record<string, unknown>>).forEach((t, idx) => {
            if (t && typeof t.text === "string") out.push({ id: n.id ?? "", widget, field: `icon_list.${idx}.text`, text: t.text, isHtml: false, set: nv => { t.text = nv; } });
          });
        }
      }
      if (Array.isArray(n.elements)) walk(n.elements);
    }
  };
  walk(elements);
  return out;
}

// Editable text blocks (id + field + readable text) for the assistant to inspect and target.
export function extractElementorBlocks(elements: unknown[]): ElBlock[] {
  return visitTextFields(elements as ElNode[])
    .map(v => ({ id: v.id, widget: v.widget, field: v.field, text: v.isHtml ? stripHtml(v.text) : v.text, isHtml: v.isHtml }))
    .filter(b => b.text.trim() !== "");
}

// All readable text on the page, in document order (headings first within each widget).
export function elementorPlainText(elements: unknown[]): string {
  return visitTextFields(elements as ElNode[])
    .map(v => (v.isHtml ? stripHtml(v.text) : v.text))
    .filter(t => t.trim() !== "")
    .join("\n");
}

// Links found in the content tree — hrefs inside rich-text fields plus widget link settings.
export function extractElementorLinks(elements: unknown[]): ElLink[] {
  const out: ElLink[] = [];
  for (const v of visitTextFields(elements as ElNode[])) {
    if (!v.isHtml) continue;
    for (const m of v.text.matchAll(/<a\b[^>]*href=["']([^"']+)["'][^>]*>([\s\S]*?)<\/a>/gi)) {
      out.push({ url: m[1], anchor: stripHtml(m[2]) });
    }
  }
  const walk = (nodes: ElNode[]): void => {
    for (const n of nodes) {
      const link = n.settings?.link as { url?: string } | undefined;
      if (link && typeof link.url === "string" && link.url) {
        out.push({ url: link.url, anchor: typeof n.settings?.text === "string" ? (n.settings!.text as string) : "" });
      }
      if (Array.isArray(n.elements)) walk(n.elements);
    }
  };
  walk(elements as ElNode[]);
  return out;
}

// Mutate the tree in place. `edits` replace a specific block's text (matched by id, optionally
// field — defaults to that element's first text field). `findReplace` swaps literal substrings
// across every text field (the reliable path for long fields, repeaters, or unlisted widgets).
// Edits run first, then find/replace. Returns how many values changed + any edit that missed.
export function applyElementorEdits(
  elements: unknown[],
  edits: ElEdit[],
  findReplace: ElFindReplace[],
): { changed: number; notFound: string[] } {
  const visits = visitTextFields(elements as ElNode[]);
  let changed = 0;
  const notFound: string[] = [];
  for (const e of edits) {
    const target = visits.find(v => v.id === e.id && (!e.field || v.field === e.field));
    if (!target) { notFound.push(e.field ? `${e.id}.${e.field}` : e.id); continue; }
    // Keep an HTML field valid when the model sends bare text (wrap it in a paragraph).
    target.set(target.isHtml && !/[<>]/.test(e.text) ? `<p>${e.text}</p>` : e.text);
    changed++;
  }
  for (const fr of findReplace) {
    if (!fr.find) continue;
    for (const v of visits) {
      if (v.text.includes(fr.find)) { v.set(v.text.split(fr.find).join(fr.replace)); changed++; }
    }
  }
  return { changed, notFound };
}
